import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccountGroup } from '../../../../shared/services/types/response';
import { ReactNode, useState } from 'react';
import { toast } from 'react-toastify';
import ConfirmDeleteModal from '../confirmDeleteModal';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteGroup, deletePersonGroup } from '../modalDetails/service';
import { maskCPFCNPJ } from '../../../../shared/utils/mask';

interface Props {
  accountGroups?: AccountGroup[] | null;
  childrenRenderer?: (group: AccountGroup) => ReactNode;
  fetchCcsDetails?: () => Promise<void>;
  status?: string;
}

const AccountGroupAccordion = ({
  accountGroups,
  childrenRenderer,
  fetchCcsDetails,
  status,
}: Props) => {
  const [showChildren, setShowChildren] = useState<Record<string, boolean>>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  if (!accountGroups || accountGroups.length === 0) {
    return (
      <Typography sx={{ mt: 2 }} color="text.secondary">
        Nenhum grupo de conta encontrado.
      </Typography>
    );
  }

  const handleDeleteClick = (id: string) => {
    setDeleteId(id);
  };

  // Função para deletar o grupo
  const handleDeleteGroup = async () => {
    if (!deleteGroupId) return;
    setDeleteLoading(true);
    console.log('o id deletado é esse esta caindo aqui ', deleteGroupId);

    const result = await deleteGroup(deleteGroupId); // Substitua por deleteAccountGroup se for outro serviço

    if (result.success) {
      toast.success('Grupo excluído com sucesso');
      setDeleteGroupId(null);

      if (fetchCcsDetails) await fetchCcsDetails();
      setDeleteLoading(false);
    } else {
      toast.error(result.message || 'Erro ao excluir grupo');
      setDeleteLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    const result = await deletePersonGroup(deleteId);

    if (result.success) {
      toast.success('Pessoa removida com sucesso');
      setDeleteId(null);
      setDeleteLoading(false);
      if (fetchCcsDetails) await fetchCcsDetails();
    } else {
      toast.error(result.message || 'Erro ao excluir pessoa');
      setDeleteLoading(false);
    }
  };

  const toggleChildren = (groupId: string) => {
    setShowChildren((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  return (
    <>
      {accountGroups.map((group, index) => (
        <Accordion key={group.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>
              Grupo {index + 1} - Situação:{' '}
              {group.account_situation === '1'
                ? 'Conta Ativa'
                : group.account_situation === '2'
                  ? 'Conta inexistente'
                  : group.account_situation === '3'
                    ? 'Conta Encerrada'
                    : group.account_situation === '4'
                      ? 'Conta sob Liminar'
                      : group.account_situation}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Typography>
                  <strong>Tipo BDV:</strong>{' '}
                  {group.type_bdv === '1'
                    ? 'Conta de Depósito'
                    : group.type_bdv === '2'
                      ? 'Conta de Poupança'
                      : group.type_bdv === '3'
                        ? 'Conta de Investimento'
                        : group.type_bdv === '4'
                          ? 'Outras Aplicações Financeiras'
                          : group.type_bdv === '5'
                            ? 'Conta de não-residente - CC5'
                            : group.type_bdv === '6'
                              ? 'Conta de Pagamento'
                              : group.type_bdv}
                </Typography>
                {status !== 'DONE' && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Impede que o accordion se expanda ao clicar no botão
                      setDeleteGroupId(group.id); // Setando o id do grupo para deletar
                    }}
                    startIcon={<DeleteIcon />}
                  >
                    Excluir grupo
                  </Button>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'start',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography>
                  <strong>Criado em:</strong>{' '}
                  {new Date(group.created_at).toLocaleString()}
                </Typography>
                <Typography>
                  <strong>Atualizado em:</strong>{' '}
                  {new Date(group.updated_at).toLocaleString()}
                </Typography>
              </Box>
            </Box>
            {group.person_groups && group.person_groups.length > 0 && (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Nome</strong>
                      </TableCell>
                      <TableCell>
                        <strong>CPF/CNPJ</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Tipo</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Vínculo BDV</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Data Início</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Data Fim</strong>
                      </TableCell>
                      {status !== 'DONE' && (
                        <TableCell>
                          <strong>Ações</strong>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.person_groups?.map((person) => (
                      <TableRow key={person.id}>
                        <TableCell>{person.person_name}</TableCell>
                        <TableCell>
                          {maskCPFCNPJ(person.cnpj_cpf_person)}
                        </TableCell>
                        <TableCell>
                          {person.type_person === 'F'
                            ? 'Pessoa Física'
                            : person.type_person === 'J'
                              ? 'Pessoa Jurídica'
                              : person.type_person}
                        </TableCell>
                        <TableCell>
                          {person.type_linked_bdv === '1'
                            ? 'Titular'
                            : person.type_linked_bdv === '2'
                              ? 'Co-Titular'
                              : person.type_linked_bdv === '3'
                                ? 'Outros (procurador, responsável ou representante)'
                                : person.type_linked_bdv}
                        </TableCell>

                        <TableCell>
                          {new Date(person.start_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {person.end_date
                            ? new Date(person.end_date).toLocaleDateString()
                            : ''}
                        </TableCell>
                        {status !== 'DONE' && (
                          <TableCell>
                            <Button
                              color="error"
                              size="small"
                              onClick={() => handleDeleteClick(person.id)}
                            >
                              <DeleteIcon />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {status !== 'DONE' && (
              <Button
                variant="contained"
                size="medium"
                sx={{ mt: 2 }}
                onClick={() => toggleChildren(group.id)}
              >
                {showChildren[group.id]
                  ? 'Ocultar formulário'
                  : 'Adicionar Nova Pessoa'}
              </Button>
            )}
            {showChildren[group.id] &&
              childrenRenderer &&
              childrenRenderer(group)}
          </AccordionDetails>
          <ConfirmDeleteModal
            open={Boolean(deleteId)}
            onClose={() => setDeleteId(null)}
            onConfirm={handleDeleteConfirm}
            itemName="esta pessoa"
            isloading={deleteLoading}
          />

          <ConfirmDeleteModal
            open={Boolean(deleteGroupId)} // Verifica se há um grupo para deletar
            onClose={() => setDeleteGroupId(null)} // Fecha o modal
            onConfirm={handleDeleteGroup} // Confirma a exclusão
            itemName="este grupo de conta" // Texto para o modal
            isloading={deleteLoading}
          />
        </Accordion>
      ))}
    </>
  );
};

export default AccountGroupAccordion;
